import { useRef, useMemo, useEffect } from 'react';
import { FormLabel, FormHelperText } from '@mui/material';
import {
  MenuDivider,
  MenuButtonBold,
  LinkBubbleMenu,
  RichTextEditor,
  MenuButtonUndo,
  MenuButtonRedo,
  MenuButtonItalic,
  MenuSelectHeading,
  MenuButtonEditLink,
  MenuButtonUnderline,
  MenuButtonAlignLeft,
  MenuButtonAlignRight,
  MenuControlsContainer,
  MenuButtonAlignCenter,
  MenuButtonImageUpload,
  type RichTextEditorRef,
  MenuButtonAlignJustify,
  MenuButtonStrikethrough,
  MenuButtonHighlightColor,
} from 'mui-tiptap';

import { registerBlob, clearFieldBlobs } from 'utils/rich-text-storage';
import {
  questionLabelText,
  questionLabelEditorContent,
} from 'utils/question-label';

import { RichTextProps } from './types';
import { containsImage } from './image-transfer';
import { useExtensions } from './use-extensions';
import {
  MenuButtonOrderedList,
  MenuButtonBulletedList,
  MenuButtonIncreaseIndent,
  MenuButtonDecreaseIndent,
} from './menu-buttons';

export const RichText = ({
  value,
  label,
  error,
  onBlur,
  editable,
  onChange,
  ariaLabel,
  fieldName,
  helperText,
  allowImages = true,
  acceptPlainText = false,
  placeholder = 'Enter text',
}: RichTextProps) => {
  const rteRef = useRef<RichTextEditorRef>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const pointerDownInside = useRef(false);
  const lastEmittedValue = useRef<string | undefined>(undefined);

  const extensions = useExtensions(allowImages, editable ? placeholder : '');
  useEffect(() => {
    if (!editable) return;
    const clearPointerDown = () => {
      pointerDownInside.current = false;
    };
    const clearOutsidePointerDown = (event: PointerEvent) => {
      // Recover when a toolbar drag ended outside the document without pointerup.
      if (!rootRef.current?.contains(event.target as Node)) clearPointerDown();
    };
    document.addEventListener('pointerdown', clearOutsidePointerDown, true);
    document.addEventListener('pointerup', clearPointerDown);
    document.addEventListener('pointercancel', clearPointerDown);
    window.addEventListener('blur', clearPointerDown);
    return () => {
      document.removeEventListener(
        'pointerdown',
        clearOutsidePointerDown,
        true
      );
      document.removeEventListener('pointerup', clearPointerDown);
      document.removeEventListener('pointercancel', clearPointerDown);
      window.removeEventListener('blur', clearPointerDown);
    };
  }, [editable]);
  const content = useMemo(
    () => (acceptPlainText ? questionLabelEditorContent(value) : (value ?? '')),
    [value, acceptPlainText]
  );

  useEffect(() => {
    // Formik echoes editor updates back as props, including '' for an empty
    // document. Only external changes should replace content and selection.
    if (
      lastEmittedValue.current !== undefined &&
      value === lastEmittedValue.current
    )
      return;
    lastEmittedValue.current = undefined;
    const editor = rteRef.current?.editor;
    if (editor && editor.getHTML() !== content) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [value, content]);

  useEffect(() => {
    return () => {
      if (fieldName) {
        clearFieldBlobs(fieldName);
      }
    };
  }, [fieldName]);

  return (
    <div
      ref={rootRef}
      className="relative w-full"
      onPointerDownCapture={(event) => {
        // Only toolbar actions preserve selection; clicking helper text or
        // the field label should mark the editor touched like a background click.
        pointerDownInside.current = !!(event.target as Element).closest(
          '.MuiTiptap-MenuBar-root'
        );
      }}
    >
      <RichTextEditor
        ref={rteRef}
        content={content}
        editable={editable}
        className="border-0!"
        extensions={extensions}
        onUpdate={({ editor }) => {
          const nextValue = editor.isEmpty ? '' : editor.getHTML();
          lastEmittedValue.current = nextValue;
          onChange?.(nextValue);
        }}
        onBlur={({ event }) => {
          // A toolbar click can have no relatedTarget (Safari). Background
          // clicks must still mark the field touched, unlike toolbar actions.
          const isInternal = event.relatedTarget
            ? rootRef.current?.contains(event.relatedTarget as Node)
            : pointerDownInside.current;
          if (!isInternal) {
            onBlur?.();
          }
        }}
        editorProps={{
          handlePaste: (_view, event) => {
            if (allowImages || !event.clipboardData) return false;
            if (containsImage(event.clipboardData)) {
              event.preventDefault();
              return true;
            }
            return false;
          },
          attributes: {
            // Tiptap defaults to textbox, so read-only previews need an explicit override.
            role: editable ? 'textbox' : 'generic',
            ...(editable && {
              'aria-multiline': 'true',
              'aria-label':
                questionLabelText(ariaLabel ?? label) || 'Rich text',
              ...(placeholder && { 'aria-placeholder': placeholder }),
            }),
          },
          handleDOMEvents: {
            drop: (_view, event) => {
              if (allowImages || !event.dataTransfer) return false;
              if (containsImage(event.dataTransfer)) {
                // Tiptap's PasteRule drop handler deletes the source selection
                // on cross-editor drags; intercept before that DOM handler runs.
                event.preventDefault();
                return true;
              }
              return false;
            },
          },
        }}
        RichTextFieldProps={{
          sx: {
            '.MuiCollapse-root': {
              borderColor: 'divider',
            },
            '&:hover .MuiTiptap-FieldContainer-notchedOutline': {
              borderColor: error ? 'error.main' : 'common.onBackgroundChannel',
            },
            '.MuiTiptap-MenuBar-root': {
              backgroundColor: 'background.paper',
              '.MuiSelect-icon': {
                color: 'text.primary',
              },
            },
            '.MuiTiptap-FieldContainer-notchedOutline': {
              opacity: error ? 1 : 0.23,
              ...(editable ? {} : { border: 'none' }),
              borderColor: error ? 'error.main' : 'common.onBackgroundChannel',
            },
            '&.MuiTiptap-FieldContainer-focused': {
              '& ~ .MuiFormLabel-root': {
                color: error ? 'error.main' : 'primary.main',
              },
              '.MuiTiptap-FieldContainer-notchedOutline': {
                opacity: 1,
                borderColor: error ? 'error.main' : 'primary.main',
              },
            },
            '.MuiTiptap-RichTextContent-root': {
              ...(editable ? {} : { padding: 0 }),
              '& ul, & ol': {
                paddingLeft: '1.5rem',
              },
              '& .ProseMirror > .is-empty:first-of-type::before': {
                height: 0,
                float: 'left',
                pointerEvents: 'none',
                color: 'text.disabled',
                content: 'attr(data-placeholder)',
              },
            },
          },
        }}
        renderControls={
          editable
            ? () => (
                <MenuControlsContainer>
                  <MenuButtonUndo />
                  <MenuButtonRedo />
                  <MenuDivider />

                  <MenuSelectHeading />

                  <MenuDivider />

                  <MenuButtonBold />
                  <MenuButtonItalic />
                  <MenuButtonUnderline />
                  <MenuButtonStrikethrough />
                  <MenuButtonHighlightColor />

                  <MenuDivider />

                  <MenuButtonAlignLeft />
                  <MenuButtonAlignCenter />
                  <MenuButtonAlignRight />
                  <MenuButtonAlignJustify />

                  <MenuDivider />

                  <MenuButtonBulletedList />
                  <MenuButtonOrderedList />

                  <MenuDivider />

                  <MenuButtonIncreaseIndent />
                  <MenuButtonDecreaseIndent />

                  <MenuDivider />

                  <MenuButtonEditLink />
                  {allowImages && (
                    <MenuButtonImageUpload
                      onUploadFiles={(files) =>
                        files.map((file) => {
                          const blobUrl = URL.createObjectURL(file);
                          if (fieldName) {
                            registerBlob(fieldName, blobUrl, file);
                          }
                          return {
                            src: blobUrl,
                            alt: file.name,
                          };
                        })
                      }
                    />
                  )}
                </MenuControlsContainer>
              )
            : undefined
        }
      >
        {() => (
          <>
            <FormLabel
              component="p"
              className="absolute! -top-[9px] left-2 z-1201 origin-top-left scale-75 px-[5px]!"
              sx={{
                backgroundColor: 'background.default',
                color: error ? 'error.main' : 'text.secondary',
              }}
            >
              {label}
            </FormLabel>
            <LinkBubbleMenu
              sx={{
                '.MuiPaper-root': {
                  backgroundColor: 'background.default',
                },
              }}
            />
          </>
        )}
      </RichTextEditor>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </div>
  );
};

export type { RichTextProps };
